import { t } from "@/lib/i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type {
  AccomplishmentView,
  DeckEvent,
} from "../../../../yap-frontend-rs/pkg";
import { Trophy, ChevronDown, PartyPopper } from "lucide-react";
import { cn } from "@/lib/utils";
import { DailyGoalEditor } from "./DailyGoalEditor";
import { TargetLanguageText } from "../../components/TargetLanguageText";

interface AccomplishmentScreenProps {
  view: AccomplishmentView;
  addEvent: (event: DeckEvent) => void;
  onDismiss: () => void;
}

export function AccomplishmentScreen({
  view,
  addEvent,
  onDismiss,
}: AccomplishmentScreenProps) {
  const targetLanguage = view.target_language;
  const dailyReviewTarget = view.target;
  const [goalOpen, setGoalOpen] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const confettiRef = useRef<confetti.CreateTypes | null>(null);

  const summary = view.today;
  const wordsKnown = view.words_known;

  // Create confetti instance bound to our canvas
  useEffect(() => {
    if (!canvasRef.current) return;
    const myConfetti = confetti.create(canvasRef.current, { resize: true });
    confettiRef.current = myConfetti;
    return () => {
      myConfetti.reset();
    };
  }, []);

  const fireConfetti = useCallback(() => {
    const fire = confettiRef.current;
    if (!fire) return;
    const end = Date.now() + 800;
    const frame = () => {
      fire({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
      });
      fire({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, []);

  // Fire on mount
  useEffect(() => {
    const timer = setTimeout(fireConfetti, 100);
    return () => clearTimeout(timer);
  }, [fireConfetti]);

  // Enter/Space to dismiss
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLButtonElement) return;
      if (e.code === "Enter" || e.code === "Space") {
        e.preventDefault();
        onDismiss();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onDismiss]);

  return (
    <div className="flex flex-col items-center gap-6">
      <canvas
        ref={canvasRef}
        className="fixed inset-0 w-full h-full pointer-events-none"
      />
      {/* Header */}
      <div className="flex flex-col items-center text-center gap-2 pt-4">
        <div className="rounded-xl bg-caution-surface p-4">
          <Trophy className="h-10 w-10 text-caution" />
        </div>
        <h2 className="text-2xl font-bold">{view.heading}</h2>
        <p className="text-muted-foreground">{view.studied}</p>
        {view.streak_label && (
          <Badge
            variant="outline"
            className="border-negative/50 text-muted-foreground font-normal text-sm"
          >
            🔥 {view.streak_label}
          </Badge>
        )}
        {/* Change goal collapsible */}
        <Collapsible
          open={goalOpen}
          onOpenChange={setGoalOpen}
          className="flex flex-col items-center"
        >
          <CollapsibleTrigger className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            {t("accomplishment-screen-change-daily-goal")}
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                goalOpen && "rotate-180",
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="flex flex-col items-center gap-3 mt-3">
            <DailyGoalEditor
              key={dailyReviewTarget}
              target={dailyReviewTarget}
              options={view.goals}
              addEvent={addEvent}
              onSave={() => setGoalOpen(false)}
            />
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* New today */}
      {summary.new_cards.length > 0 && (
        <div className="w-full">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {t("accomplishment-screen-new-today")}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {summary.new_cards.map((card, i) => (
              <Card key={i} className="p-3 gap-0">
                <div className="flex justify-between items-start">
                  <p className="font-semibold">
                    <TargetLanguageText language={targetLanguage}>
                      {card.word}
                    </TargetLanguageText>
                  </p>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
                    {card.card_type}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {card.translation}
                </p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Learned (graduated to Review state today) */}
      {summary.learned_cards.length > 0 && (
        <div className="w-full">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {t("common-learned")}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {summary.learned_cards.map((card, i) => (
              <Card key={i} className="p-3 gap-0">
                <div className="flex justify-between items-start">
                  <p className="font-semibold">
                    <TargetLanguageText language={targetLanguage}>
                      {card.word}
                    </TargetLanguageText>
                  </p>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
                    {card.card_type}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {card.translation}
                </p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Back on track (relearning → review) */}
      {summary.locked_in_cards.length > 0 && (
        <div className="w-full">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {t("accomplishment-screen-back-on-track")}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {summary.locked_in_cards.map((card, i) => (
              <Card key={i} className="p-3 gap-0">
                <div className="flex justify-between items-start">
                  <p className="font-semibold">
                    <TargetLanguageText language={targetLanguage}>
                      {card.word}
                    </TargetLanguageText>
                  </p>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
                    {card.card_type}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {card.translation}
                </p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Reviewed */}
      {summary.reviewed_words.length > 0 && (
        <div className="w-full">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {t("accomplishment-reviewed", {
              count: summary.reviewed_words.length,
            })}
          </p>
          <Card variant="light" className="p-3 gap-0" animate>
            <div className="flex flex-wrap gap-2">
              {summary.reviewed_words.map((word, i) => (
                <Badge key={i} variant="outline">
                  <TargetLanguageText language={targetLanguage}>
                    {word}
                  </TargetLanguageText>
                </Badge>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-2 w-full">
        {summary.recall_percent != null && (
          <Card variant="light" className="p-3 gap-0 text-center">
            <p className="text-2xl font-bold">{summary.recall_percent}%</p>
            <p className="text-xs text-muted-foreground">
              {t("accomplishment-screen-recall")}
            </p>
          </Card>
        )}
        <Card variant="light" className="p-3 gap-0 text-center">
          <p className="text-2xl font-bold">{wordsKnown}</p>
          <p className="text-xs text-muted-foreground">
            {t("accomplishment-screen-words-known", { count: wordsKnown })}
          </p>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 w-full sticky bottom-0 py-3">
        <Button
          variant="outline"
          size="icon"
          onClick={fireConfetti}
          className="shrink-0"
        >
          <PartyPopper className="h-5 w-5" />
        </Button>
        <Button onClick={onDismiss} size="lg" className="flex-1">
          {t("common-continue")}
        </Button>
      </div>
    </div>
  );
}
